import { useState, useCallback, useMemo } from 'react'
import { SortByColumn, SortDirection } from '../queries'

export interface ActiveSort {
  column: SortByColumn
  direction: SortDirection
}

export type ColumnSortState = Record<SortByColumn, SortDirection | null>

const SORTABLE_COLUMNS = Object.values(SortByColumn)

export const useColumnSort = () => {
  const [sortState, setSortState] = useState<ColumnSortState>(() =>
    SORTABLE_COLUMNS.reduce(
      (acc, col) => ({ ...acc, [col]: null }),
      {} as ColumnSortState
    )
  )

  const toggleSort = useCallback((column: SortByColumn) => {
    setSortState((prev) => {
      const current = prev[column]
      let next: SortDirection | null

      if (current === null) {
        next = SortDirection.ASC
      } else if (current === SortDirection.ASC) {
        next = SortDirection.DESC
      } else {
        next = null
      }

      // Reset all columns, then set only the toggled one
      const reset = SORTABLE_COLUMNS.reduce(
        (acc, col) => ({ ...acc, [col]: null }),
        {} as ColumnSortState
      )

      return { ...reset, [column]: next }
    })
  }, [])

  const activeSort = useMemo((): ActiveSort | null => {
    const entry = SORTABLE_COLUMNS.find((col) => sortState[col] !== null)

    if (entry && sortState[entry]) {
      return { column: entry, direction: sortState[entry] }
    }

    return null
  }, [sortState])

  const getSortIcon = useCallback(
    (column: SortByColumn): 'sort' | 'sort-up' | 'sort-down' => {
      const direction = sortState[column]

      if (direction === SortDirection.ASC) return 'sort-up'
      if (direction === SortDirection.DESC) return 'sort-down'

      return 'sort'
    },
    [sortState]
  )

  const getAriaSort = useCallback(
    (column: SortByColumn): 'ascending' | 'descending' | 'none' => {
      const direction = sortState[column]

      if (direction === SortDirection.ASC) return 'ascending'
      if (direction === SortDirection.DESC) return 'descending'

      return 'none'
    },
    [sortState]
  )

  const resetSort = useCallback(() => {
    setSortState(
      SORTABLE_COLUMNS.reduce(
        (acc, col) => ({ ...acc, [col]: null }),
        {} as ColumnSortState
      )
    )
  }, [])

  return {
    sortState,
    activeSort,
    toggleSort,
    getSortIcon,
    getAriaSort,
    resetSort,
  }
}
